import { createSlice } from '@reduxjs/toolkit';
import { RequestStatus } from '../../../../shared/enums/request-status';
import {
  getMcpSessionsRequest,
  revokeMcpSessionRequest,
  createMcpApiKeyRequest,
  fetchMcpApiKeyRequest,
  MCP_API_KEY_LABEL,
} from './extra-actions';
import { McpSession } from './types';

interface RequestState {
  status: RequestStatus;
  message: string;
  error: any;
}

interface State {
  getMcpSessionsRequest: RequestState;
  revokeMcpSessionRequest: RequestState;
  createMcpApiKeyRequest: RequestState;
  fetchMcpApiKeyRequest: RequestState;
  sessions: McpSession[];
  generatedApiToken: string;
  mcpApiKeyExists: boolean;
  isLoading: boolean;
}

const initialState: State = {
  getMcpSessionsRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  revokeMcpSessionRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  createMcpApiKeyRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  fetchMcpApiKeyRequest: {
    status: RequestStatus.Ideal,
    message: null,
    error: null,
  },
  sessions: [],
  generatedApiToken: '',
  mcpApiKeyExists: false,
  isLoading: false,
};

const mcpSettingsSlice = createSlice({
  name: 'mcpSettings',
  initialState,
  reducers: {
    resetRevokeMcpSessionRequest: (state) => {
      state.revokeMcpSessionRequest = initialState.revokeMcpSessionRequest;
    },
    resetCreateMcpApiKeyRequest: (state) => {
      state.createMcpApiKeyRequest = initialState.createMcpApiKeyRequest;
      state.fetchMcpApiKeyRequest = initialState.fetchMcpApiKeyRequest;
      state.generatedApiToken = '';
      state.mcpApiKeyExists = false;
    },
  },
  extraReducers: (builder) => {
    // Get MCP Sessions
    builder.addCase(getMcpSessionsRequest.pending, (state) => {
      state.getMcpSessionsRequest.status = RequestStatus.Pending;
      state.getMcpSessionsRequest.error = null;
      state.isLoading = true;
    });
    builder.addCase(getMcpSessionsRequest.fulfilled, (state, action) => {
      state.getMcpSessionsRequest.status = RequestStatus.Succeeded;
      state.getMcpSessionsRequest.message = action.payload.message;
      state.sessions = action.payload.payload;
      state.isLoading = false;
    });
    builder.addCase(getMcpSessionsRequest.rejected, (state, action) => {
      state.getMcpSessionsRequest.status = RequestStatus.Failed;
      state.getMcpSessionsRequest.error = action.payload;
      state.isLoading = false;
    });

    // Fetch existing MCP API Key
    builder.addCase(fetchMcpApiKeyRequest.pending, (state) => {
      state.fetchMcpApiKeyRequest.status = RequestStatus.Pending;
      state.fetchMcpApiKeyRequest.error = null;
    });
    builder.addCase(fetchMcpApiKeyRequest.fulfilled, (state, action) => {
      state.fetchMcpApiKeyRequest.status = RequestStatus.Succeeded;
      const tokens = action.payload.payload || [];
      const mcpToken = tokens.find(
        (token: { label: string }) => token.label === MCP_API_KEY_LABEL,
      );
      state.generatedApiToken = mcpToken?.hashToken || '';
      state.mcpApiKeyExists = !!mcpToken;
    });
    builder.addCase(fetchMcpApiKeyRequest.rejected, (state, action) => {
      state.fetchMcpApiKeyRequest.status = RequestStatus.Failed;
      state.fetchMcpApiKeyRequest.error = action.payload;
    });

    // Create MCP API Key
    builder.addCase(createMcpApiKeyRequest.pending, (state) => {
      state.createMcpApiKeyRequest.status = RequestStatus.Pending;
      state.createMcpApiKeyRequest.error = null;
    });
    builder.addCase(createMcpApiKeyRequest.fulfilled, (state, action) => {
      state.createMcpApiKeyRequest.status = RequestStatus.Succeeded;
      state.createMcpApiKeyRequest.message = action.payload.message;
      state.generatedApiToken = action.payload.payload.hashToken;
      state.mcpApiKeyExists = true;
    });
    builder.addCase(createMcpApiKeyRequest.rejected, (state, action) => {
      state.createMcpApiKeyRequest.status = RequestStatus.Failed;
      state.createMcpApiKeyRequest.error = action.payload;
    });

    // Revoke MCP Session
    builder.addCase(revokeMcpSessionRequest.pending, (state) => {
      state.revokeMcpSessionRequest.status = RequestStatus.Pending;
      state.revokeMcpSessionRequest.error = null;
    });
    builder.addCase(revokeMcpSessionRequest.fulfilled, (state, action) => {
      state.revokeMcpSessionRequest.status = RequestStatus.Succeeded;
      state.revokeMcpSessionRequest.message = action.payload.message;
    });
    builder.addCase(revokeMcpSessionRequest.rejected, (state, action) => {
      state.revokeMcpSessionRequest.status = RequestStatus.Failed;
      state.revokeMcpSessionRequest.error = action.payload;
    });
  },
});

export const {
  resetRevokeMcpSessionRequest,
  resetCreateMcpApiKeyRequest,
} = mcpSettingsSlice.actions;

export default mcpSettingsSlice.reducer;
